import { BookedQuote } from "../providers/bookedQuotesProvider";
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import * as React from "react";
import { useState } from "react";
import { Bundle, catalogProvider } from "../providers/catalogProvider";
import { Labeled, useRefresh } from "react-admin";
import {
  CAR_ONLY_DRIVING_LICENSE_CLASSES,
  CAR_WITH_TRAILER_DRIVING_LICENSE_CLASSES,
  DrivingLicenseClass,
  MOTORCYCLE_DRIVING_LICENSE_CLASSES,
} from "../model/DrivingLicenseClass";
import { areSetsEqual, postCondition, reportError } from "../backoffice.utils";
import { studentsProvider } from "../providers/studentsProvider";
import { PricesOverview } from "../misc/PricesOverview";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { atom, useRecoilState } from "recoil";
import { Row } from "../misc/Row";
import { Column } from "../misc/Column";
import { grants } from "../backoffice.access_control";
import { ChangePricesForm } from "../details/student/ChangePricesForm";
import { BundleAutocomplete } from "../inputs/BundleAutocomplete";

export const bookedQuoteShownInBookedQuoteDialogState = atom<null | BookedQuote>({
  key: "bookedQuoteShownInBookedQuoteDialog",
  default: null,
  effects: [
    ({ onSet }) => {
      onSet((newValue) => {
        const url = new URL(location.href);
        if (newValue) {
          if (url.searchParams.get("openDialog") !== `BookedQuoteDialog!${newValue.id}`) {
            url.searchParams.set("openDialog", `BookedQuoteDialog!${newValue.id}`);
            browserHistory.replace(url.toString());
          }
        } else if (url.searchParams.has("openDialog")) {
          url.searchParams.delete("openDialog");
          browserHistory.replace(url.toString());
        }
      });
    },
  ],
});

type BookedQuoteDialogState =
  | "prices overview"
  | "change driving license class"
  | "change prices > form"
  | "replace bundle"
  | "replace bundle > new prices overview";

export const bookedQuoteDialogInitialState = atom<BookedQuoteDialogState>({
  key: "bookedQuoteDialogInitialState",
  default: "prices overview",
});

export function BookedQuoteDialog() {
  const [bookedQuote, setBookedQuoteShownInDialog] = useRecoilState(bookedQuoteShownInBookedQuoteDialogState);
  const [initialDialogState, setInitialDialogState] = useRecoilState(bookedQuoteDialogInitialState);
  const closeDialog = () => {
    setBookedQuoteShownInDialog(null);
    setInitialDialogState("prices overview");
  };

  if (bookedQuote) {
    return (
      <_BookedQuoteDialog bookedQuote={bookedQuote} initialDialogState={initialDialogState} closeDialog={closeDialog} />
    );
  } else {
    return null;
  }
}

function _BookedQuoteDialog({
  bookedQuote,
  initialDialogState,
  closeDialog,
}: {
  bookedQuote: BookedQuote;
  initialDialogState: BookedQuoteDialogState;
  closeDialog: () => void;
}) {
  const [dialogState, setDialogState] = useState<BookedQuoteDialogState>(initialDialogState);
  const [selectedDrivingLicenseClasses, setSelectedDrivingLicenseClasses] = useState(bookedQuote.drivingLicenseClasses);
  const [bundleFilterDrivingLicenseClasses, setBundleFilterDrivingLicenseClasses] = useState<DrivingLicenseClass[]>([]);
  const [selectedBundle, setSelectedBundle] = useState<Bundle | undefined>(undefined);
  const refresh = useRefresh();
  const [saving, setSaving] = useState(false);
  const canChangePrices = grants.has("Quote:book");

  const changeDrivingLicenseClasses = async () => {
    setSaving(true);
    try {
      const changes = bookedQuote.drivingLicenseClasses
        .map((a, i) => [a, selectedDrivingLicenseClasses[i]])
        .filter(([a, b]) => a !== b);
      if (changes.length === 0) {
        throw new Error("Invalid state: No driving license class change");
      }
      await catalogProvider.changeDrivingLicenseClasses({
        studentId: bookedQuote.studentId,
        quoteId: bookedQuote.id,
        changes: Object.fromEntries(changes),
      });
      await postCondition(async () => {
        const { data: student } = await studentsProvider.getOne("students", { id: bookedQuote.studentId });
        const expectedDrivingLicenseClasses = changes.map(([_, newDrivingLicenseClass]) => newDrivingLicenseClass);
        const actualDrivingLicenseClasses = new Set(student.bookedTrainings.map((it) => it.drivingLicenseClass));
        return expectedDrivingLicenseClasses.every((it) => actualDrivingLicenseClasses.has(it));
      });
      if (changes.length === 1) {
        showInfoToast(`Führerscheinklasse erfolgreich von ${changes[0][0]} auf ${changes[0][1]} geändert.`);
      } else {
        showInfoToast(`Führerscheinklassen erfolgreich geändert.`);
      }
      refresh();
    } catch (error) {
      reportError("Failed to change driving license classes", error);
      showErrorToast("Oje, das hat leider nicht geklappt.");
    } finally {
      closeDialog();
    }
  };

  const changeBundle = async () => {
    setSaving(true);
    try {
      const newBundle = selectedBundle;
      if (!newBundle) {
        throw new Error("Invalid state: No bundle selected");
      }
      await catalogProvider.changeBundle({
        studentId: bookedQuote.studentId,
        oldQuoteId: bookedQuote.id,
        newBundleId: newBundle.id,
      });
      await postCondition(async () => {
        const { data: student } = await studentsProvider.getOne("students", { id: bookedQuote.studentId });
        return student.bookedTrainings.some((it) => it.bundleName === newBundle.name);
      });
      showInfoToast(`Paket ${bookedQuote.bundleName} erfolgreich durch Paket ${newBundle.name} ersetzt.`);
      refresh();
    } catch (error) {
      reportError("Failed to change bundle", error);
      showErrorToast("Oje, das hat leider nicht geklappt.");
    } finally {
      closeDialog();
    }
  };

  return (
    <Dialog
      open
      onClose={closeDialog}
      maxWidth={
        dialogState === "prices overview" ||
        dialogState === "change driving license class" ||
        dialogState === "replace bundle"
          ? "sm"
          : "lg"
      }
      fullWidth
    >
      {dialogState === "prices overview" && (
        <>
          <DialogTitle sx={{ mr: "100px" }}>{bookedQuote.bundleName}</DialogTitle>
          <DialogCloseButton onClick={closeDialog} />
          <DialogContent>
            <Column spacing={1}>
              {dialogState === "prices overview" && bookedQuote.drivingLicenseClasses.length > 0 && (
                <Row spacing={1} sx={{ alignItems: "start" }}>
                  <Labeled
                    label={
                      bookedQuote.drivingLicenseClasses.length === 1 ? "Führerscheinklasse" : "Führerscheinklassen"
                    }
                  >
                    <Row spacing={1}>
                      {bookedQuote.drivingLicenseClasses.map((it) => (
                        <Chip key={it} label={it} />
                      ))}
                    </Row>
                  </Labeled>
                </Row>
              )}
              {dialogState === "prices overview" && (
                <Box sx={{ paddingTop: "22px", width: "100%" }}>
                  <Labeled label="Preise" sx={{ width: "100%" }}>
                    <PricesOverview bookedQuote={bookedQuote} />
                  </Labeled>
                </Box>
              )}
            </Column>
          </DialogContent>
          <DialogActions>
            {canChangePrices && (
              <Button variant="outlined" onClick={() => setDialogState("change prices > form")}>
                Preise ändern
              </Button>
            )}
          </DialogActions>
        </>
      )}
      {dialogState === "change driving license class" && (
        <>
          <DialogTitle sx={{ mr: "100px" }}>
            {bookedQuote.drivingLicenseClasses.length === 1
              ? "Führerscheinklasse ändern"
              : "Führerscheinklasse(n) ändern"}
          </DialogTitle>
          <DialogCloseButton onClick={closeDialog} />
          <DialogContent>
            <Column spacing="1em">
              <_ChooseDrivingLicenseClassList
                bookedQuote={bookedQuote}
                selectedDrivingLicenseClasses={selectedDrivingLicenseClasses}
                setSelectedDrivingLicenseClasses={setSelectedDrivingLicenseClasses}
              />
              <DialogContentText sx={{ fontStyle: "italic" }}>Hinweis: Preise bleiben gleich.</DialogContentText>
            </Column>
          </DialogContent>
          <DialogActions>
            <_BackButton onClick={() => setDialogState("prices overview")} />
            <LoadingButton
              variant="contained"
              loading={saving}
              disabled={areSetsEqual(
                new Set(bookedQuote.drivingLicenseClasses),
                new Set(selectedDrivingLicenseClasses),
              )}
              onClick={changeDrivingLicenseClasses}
            >
              Speichern
            </LoadingButton>
          </DialogActions>
        </>
      )}
      {dialogState === "replace bundle" && (
        <>
          <DialogTitle sx={{ mr: "100px" }}>Paket auswählen</DialogTitle>
          <DialogCloseButton onClick={closeDialog} />
          <DialogContent>
            <BundleAutocomplete
              drivingSchoolId={bookedQuote.drivingSchoolId}
              drivingLicenseClasses={bundleFilterDrivingLicenseClasses}
              onDrivingLicenseClassesChange={setBundleFilterDrivingLicenseClasses}
              value={selectedBundle}
              onChange={setSelectedBundle}
              filterBundle={(candidate) => {
                const candidateDrivingLicenseClasses = candidate.trainings.map(
                  (training) => training.drivingLicenseClass,
                );
                return (
                  candidate.name !== bookedQuote.bundleName &&
                  candidateDrivingLicenseClasses.length === bookedQuote.drivingLicenseClasses.length &&
                  candidateDrivingLicenseClasses.every((candidateClass) =>
                    bookedQuote.drivingLicenseClasses.some((bookedClass) =>
                      _areCompatible(candidateClass, bookedClass),
                    ),
                  )
                );
              }}
            />
          </DialogContent>
          <DialogActions>
            <_BackButton onClick={() => setDialogState("prices overview")} />
            <Button
              variant="outlined"
              endIcon={<NavigateNextIcon />}
              disabled={!selectedBundle}
              onClick={() => setDialogState("replace bundle > new prices overview")}
            >
              Weiter zur Preisübersicht
            </Button>
          </DialogActions>
        </>
      )}
      {dialogState === "replace bundle > new prices overview" && (
        <>
          <DialogTitle sx={{ mr: "100px" }}>
            Paket "{bookedQuote.bundleName}" durch Paket "{selectedBundle!.name}" ersetzen
          </DialogTitle>
          <DialogCloseButton onClick={closeDialog} />
          <DialogContent>
            <PricesOverview bookedQuote={bookedQuote} newBundle={selectedBundle} />
          </DialogContent>
          <DialogActions>
            <_BackButton
              onClick={() => {
                setSelectedBundle(undefined);
                setDialogState(dialogState.substring(0, dialogState.indexOf(" >")) as BookedQuoteDialogState);
              }}
            />
            <LoadingButton variant="contained" loading={saving} onClick={changeBundle}>
              Speichern
            </LoadingButton>
          </DialogActions>
        </>
      )}
      {dialogState === "change prices > form" && (
        <>
          <DialogTitle sx={{ mr: "100px" }}>Preise ändern</DialogTitle>
          <DialogCloseButton onClick={closeDialog} />
          <ChangePricesForm
            bookedQuote={bookedQuote}
            backButton={<_BackButton onClick={() => setDialogState("prices overview")} />}
            onSuccess={closeDialog}
          />
        </>
      )}
    </Dialog>
  );
}

function _BackButton(props: { onClick: () => void }) {
  return (
    <Button startIcon={<NavigateBeforeIcon />} variant="outlined" onClick={props.onClick}>
      Zurück
    </Button>
  );
}

function _ChooseDrivingLicenseClassList({
  bookedQuote,
  selectedDrivingLicenseClasses,
  setSelectedDrivingLicenseClasses,
}: {
  bookedQuote: BookedQuote;
  selectedDrivingLicenseClasses: Array<DrivingLicenseClass>;
  setSelectedDrivingLicenseClasses: (selection: Array<DrivingLicenseClass>) => void;
}) {
  return (
    <>
      <DialogContentText sx={{ mt: "1em", mb: "1em" }}>
        {bookedQuote.trainingIds.length === 1
          ? "Bitte wähle die neue Führerscheinklasse aus:"
          : "Bitte wähle die neuen Führerscheinklassen aus:"}
      </DialogContentText>
      <Stack direction="column" spacing={2}>
        {bookedQuote.drivingLicenseClasses.map((drivingLicenseClass, index) => (
          <_ChooseDrivingLicenseClassListItem
            key={drivingLicenseClass}
            oldValue={drivingLicenseClass}
            newValue={selectedDrivingLicenseClasses[index]}
            onChange={(newValue) => {
              const a = selectedDrivingLicenseClasses.map((v, i) => (i === index ? newValue : v));
              setSelectedDrivingLicenseClasses(a);
            }}
          />
        ))}
      </Stack>
    </>
  );
}

function _ChooseDrivingLicenseClassListItem({
  oldValue,
  newValue,
  onChange,
}: {
  oldValue: DrivingLicenseClass;
  newValue: DrivingLicenseClass;
  onChange: (value: DrivingLicenseClass) => void;
}) {
  const drivingLicenseClasses = _getAlternativeDrivingLicenseClasses(oldValue);
  return (
    <Container>
      <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
        <span>{oldValue}</span>
        <span>{"➔"}</span>
        <Select value={newValue} onChange={({ target: { value } }) => onChange(value as DrivingLicenseClass)}>
          {drivingLicenseClasses.map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    </Container>
  );
}

function _getAlternativeDrivingLicenseClasses(drivingLicenseClass: DrivingLicenseClass): Array<DrivingLicenseClass> {
  if (CAR_ONLY_DRIVING_LICENSE_CLASSES.has(drivingLicenseClass)) {
    return [...CAR_ONLY_DRIVING_LICENSE_CLASSES];
  } else if (CAR_WITH_TRAILER_DRIVING_LICENSE_CLASSES.has(drivingLicenseClass)) {
    return [...CAR_WITH_TRAILER_DRIVING_LICENSE_CLASSES];
  } else if (MOTORCYCLE_DRIVING_LICENSE_CLASSES.has(drivingLicenseClass)) {
    return [...MOTORCYCLE_DRIVING_LICENSE_CLASSES];
  } else {
    return [drivingLicenseClass];
  }
}

function _areCompatible(a: DrivingLicenseClass, b: DrivingLicenseClass) {
  return (
    a === b ||
    (CAR_ONLY_DRIVING_LICENSE_CLASSES.has(a) && CAR_ONLY_DRIVING_LICENSE_CLASSES.has(b)) ||
    (CAR_WITH_TRAILER_DRIVING_LICENSE_CLASSES.has(a) && CAR_WITH_TRAILER_DRIVING_LICENSE_CLASSES.has(b)) ||
    (MOTORCYCLE_DRIVING_LICENSE_CLASSES.has(a) && MOTORCYCLE_DRIVING_LICENSE_CLASSES.has(b))
  );
}
